import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { useTheme } from '@/stores/theme.store'
import { CenterSwitcher } from '@/components/CenterSwitcher'
import { authApi } from '@/api/auth'
import { HomeIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline'
import {
  getPrimaryNav, getAiEntry, getCrmEntry, getUtilityNav, buildNavCtx, type NavEntry,
} from '@/config/navModel'

interface Props { open: boolean; onClose: () => void }

// Lo `end` serve solo dove una voce è prefisso di un'altra (es. /app/divecenter
// rispetto a /app/divecenter/bookings): così il genitore non resta sempre attivo.
function needsEnd(to: string, all: NavEntry[]): boolean {
  return all.some(e => e.to !== to && e.to.startsWith(to + '/'))
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 py-2 rounded-lg text-sm transition-colors ${
    isActive
      ? 'border-l-4 border-l-ocean-400 bg-white/15 text-white pl-2 pr-3 font-medium'
      : 'px-3 text-white/80 hover:bg-white/10 hover:text-white'
  }`

export default function Sidebar({ open, onClose }: Props) {
  const { t } = useTranslation()
  const profileType   = useAuthStore(s => s.profileType)
  const hasCapability = useAuthStore(s => s.hasCapability)
  const isAdmin       = useAuthStore(s => s.isAdminUser)
  const isSales       = useAuthStore(s => s.isSalesUser)
  const user          = useAuthStore(s => s.user)
  const logout        = useAuthStore(s => s.logout)
  const { resolved, toggle } = useTheme()

  const ctx = buildNavCtx({ profileType: profileType ?? null, isAdmin, isSales, hasCapability, roles: user?.roles })

  // Voci primarie del ruolo: STESSA sorgente del BottomNav (desktop ⟷ mobile allineati).
  const primary = getPrimaryNav(ctx, t)
  // Voci trasversali: CRM (solo sales/admin) e Assistente AI (tutti).
  const crossCutting: NavEntry[] = [
    ...(isSales ? [getCrmEntry(t)] : []),
    getAiEntry(t),
  ]
  const utility = getUtilityNav(t)

  function renderLink({ to, label, icon: Icon }: NavEntry, allForEnd: NavEntry[]) {
    return (
      <NavLink key={to} to={to} end={to === '/app/dashboard' || needsEnd(to, allForEnd)} onClick={onClose} className={linkClass}>
        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        {label}
      </NavLink>
    )
  }

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-30 w-60 flex-shrink-0 flex flex-col
        bg-gray-900 text-white transition-transform duration-200
        lg:relative lg:translate-x-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
        <img src="/logo.svg" alt="eDive" className="w-9 h-9 flex-shrink-0" />
        <div>
          <div className="font-black text-white leading-none tracking-tight" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>
            eDive<span className="text-cyan-400 font-semibold">{t('auto.componentsSidebar.tech')}</span>
          </div>
          {profileType && (
            <div className="text-xs text-white/40 mt-0.5">
              {t(`profile.profileTypes.${profileType}` as any)}
            </div>
          )}
        </div>
      </div>

      {/* Center switcher — solo staff dive center */}
      {profileType === 'DIVE_CENTER' && (
        <div className="px-2 pb-2 border-b border-white/10">
          <CenterSwitcher />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {/* Dashboard — sempre primo */}
        <NavLink to="/app/dashboard" end onClick={onClose} className={({ isActive }) =>
          `flex items-center gap-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1 ${
            isActive
              ? 'border-l-4 border-l-ocean-400 bg-white/15 text-white pl-2 pr-3'
              : 'px-3 text-white/80 hover:bg-white/10 hover:text-white'
          }`
        }>
          <HomeIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          {t('nav.dashboard')}
        </NavLink>

        {/* Voci primarie del ruolo */}
        {primary.map(item => renderLink(item, primary))}

        {crossCutting.length > 0 && <div className="border-t border-white/10 my-2" />}
        {crossCutting.map(item => renderLink(item, crossCutting))}

        <div className="border-t border-white/10 my-2" />

        {utility.map(item => renderLink(item, utility))}
      </nav>

      {/* Tema + Logout */}
      <div className="px-2 pb-3 border-t border-white/10 pt-2 space-y-0.5">
        <button
          onClick={toggle}
          aria-label={resolved === 'dark' ? t('theme.toLight', 'Passa al tema chiaro') : t('theme.toDark', 'Passa al tema scuro')}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
        >
          {resolved === 'dark'
            ? <SunIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            : <MoonIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />}
          {resolved === 'dark' ? t('theme.light', 'Tema chiaro') : t('theme.dark', 'Tema scuro')}
        </button>
        <button
          onClick={async () => { await authApi.logout().catch(() => {}); logout() }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-red-700 hover:text-white transition-colors"
        >
          {t('auth.logout')}
        </button>
      </div>
    </aside>
  )
}
